import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, Calendar, Flag, CheckSquare } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { differenceInDays, isPast, parseISO } from "date-fns";
import { format } from "date-fns";
import { nb } from "date-fns/locale";

export default function DeadlineNotifications() {
  const { data: tasks = [] } = useQuery({
    queryKey: ["tasks"],
    queryFn: () => base44.entities.Task.list(),
  });

  const { data: milestones = [] } = useQuery({
    queryKey: ["milestones"],
    queryFn: () => base44.entities.Milestone.list(),
  });

  const now = new Date();
  const upcomingThreshold = 7; // 7 days

  // Filter tasks with upcoming or overdue deadlines
  const upcomingTasks = tasks.filter(task => {
    if (!task.due_date || task.status === "completed") return false;
    const daysUntil = differenceInDays(parseISO(task.due_date), now);
    return daysUntil >= -1 && daysUntil <= upcomingThreshold;
  }).sort((a, b) => parseISO(a.due_date) - parseISO(b.due_date));

  // Filter milestones with upcoming or overdue deadlines
  const upcomingMilestones = milestones.filter(milestone => {
    if (!milestone.due_date || milestone.status === "completed") return false;
    const daysUntil = differenceInDays(parseISO(milestone.due_date), now);
    return daysUntil >= -1 && daysUntil <= upcomingThreshold;
  }).sort((a, b) => parseISO(a.due_date) - parseISO(b.due_date));

  const overdueTasks = upcomingTasks.filter(t => isPast(parseISO(t.due_date)));
  const overdueMilestones = upcomingMilestones.filter(m => isPast(parseISO(m.due_date)));

  if (upcomingTasks.length === 0 && upcomingMilestones.length === 0) return null;

  const getDaysText = (date) => {
    const days = differenceInDays(parseISO(date), now);
    if (days < 0) return `${Math.abs(days)} dag${Math.abs(days) !== 1 ? 'er' : ''} forsinket`;
    if (days === 0) return "I dag";
    if (days === 1) return "I morgen";
    return `Om ${days} dager`;
  };

  return (
    <Card className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
      <div className="flex items-center gap-2 mb-4">
        <AlertCircle className="w-5 h-5 text-amber-600" />
        <h3 className="font-semibold text-slate-900">Kommende Deadlines</h3>
        {(overdueTasks.length > 0 || overdueMilestones.length > 0) && (
          <Badge className="bg-rose-100 text-rose-700 border-0">
            {overdueTasks.length + overdueMilestones.length} forsinket
          </Badge>
        )}
      </div>

      <div className="space-y-2">
        {/* Milestones */}
        {upcomingMilestones.map((milestone) => {
          const isOverdue = isPast(parseISO(milestone.due_date));
          return (
            <div
              key={milestone.id}
              className={`p-3 rounded-lg ${isOverdue ? "bg-rose-100" : "bg-white"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1">
                  <Flag className={`w-4 h-4 mt-0.5 ${isOverdue ? "text-rose-600" : "text-amber-600"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{milestone.name}</p>
                    <p className="text-xs text-slate-600 truncate">{milestone.project_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={`text-xs border-0 ${isOverdue ? "bg-rose-200 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                    {getDaysText(milestone.due_date)}
                  </Badge>
                  <p className="text-xs text-slate-500 mt-1">
                    {format(parseISO(milestone.due_date), "dd MMM", { locale: nb })}
                  </p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Tasks */}
        {upcomingTasks.slice(0, 5).map((task) => {
          const isOverdue = isPast(parseISO(task.due_date));
          return (
            <div
              key={task.id}
              className={`p-3 rounded-lg ${isOverdue ? "bg-rose-100" : "bg-white"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1">
                  <CheckSquare className={`w-4 h-4 mt-0.5 ${isOverdue ? "text-rose-600" : "text-slate-600"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{task.title}</p>
                    <p className="text-xs text-slate-600 truncate">{task.assigned_to_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge className={`text-xs border-0 ${isOverdue ? "bg-rose-200 text-rose-800" : "bg-blue-100 text-blue-800"}`}>
                    {getDaysText(task.due_date)}
                  </Badge>
                  <p className="text-xs text-slate-500 mt-1">
                    {format(parseISO(task.due_date), "dd MMM", { locale: nb })}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {upcomingTasks.length > 5 && (
        <p className="text-xs text-slate-500 mt-3 text-center">
          +{upcomingTasks.length - 5} flere oppgaver
        </p>
      )}
    </Card>
  );
}